import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import { useTheme, useMediaQuery } from "@mui/material";
import Card from "@mui/material/Card";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import BigButtonDashboard from "components/BigButtonDashboard";

import { useAuth } from "contexts/AuthContext";
import { propertyService, leaseService, paymentService, maintenanceService } from "services/firestoreService";

function Default() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, getUserDisplayName } = useAuth();

  const [stats, setStats] = useState({
    properties: 0,
    activeLeases: 0,
    occupancy: 0,
    rentCollected: 0,
    rentDue: 0,
    openTickets: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchStats = async () => {
      try {
        const [properties, leases, payments, maintenance] = await Promise.all([
          propertyService.getAll(user.uid),
          leaseService.getAll(user.uid),
          paymentService.getAll(user.uid),
          maintenanceService.getAll(user.uid),
        ]);

        const activeLeases = leases.filter((l) => l.status === "active" || l.status === "Active");
        const totalUnits = properties.reduce((sum, p) => sum + (p.units || 1), 0);
        const occupancy = totalUnits > 0 ? Math.round((activeLeases.length / totalUnits) * 100) : 0;

        const collected = payments
          .filter((p) => p.status === "paid")
          .reduce((sum, p) => sum + (p.amount || 0), 0);
        const due = payments
          .filter((p) => p.status === "pending" || p.status === "overdue")
          .reduce((sum, p) => sum + (p.amount || 0), 0);

        const openTickets = maintenance.filter(
          (m) => m.status === "Open" || m.status === "In Progress" || m.status === "open"
        ).length;

        setStats({
          properties: properties.length,
          activeLeases: activeLeases.length,
          occupancy,
          rentCollected: collected,
          rentDue: due,
          openTickets,
        });
      } catch (err) {
        console.error("Failed to fetch dashboard stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  const formatCurrency = (val) =>
    val >= 1000 ? `$${(val / 1000).toFixed(val >= 10000 ? 0 : 1)}k` : `$${val}`;

  // eslint-disable-next-line react/prop-types
  const StatCard = ({ label, value, color, bg }) => (
    <ArgonBox p={2} borderRadius="lg" sx={{ backgroundColor: bg }}>
      <ArgonTypography variant="body2" color={color} fontWeight="medium" mb={0.5}>
        {label}
      </ArgonTypography>
      <ArgonTypography variant="h4" fontWeight="bold">
        {loading ? "..." : value}
      </ArgonTypography>
    </ArgonBox>
  );

  if (isMobile) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={2}>
          <ArgonBox px={2} pb={2} textAlign="center">
            <ArgonTypography variant="h4" fontWeight="bold">
              Welcome, {getUserDisplayName()}
            </ArgonTypography>
            <ArgonTypography variant="body2" color="text">
              What would you like to do today?
            </ArgonTypography>
          </ArgonBox>

          <BigButtonDashboard />

          <ArgonBox px={2} mt={3}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StatCard label="Occupancy" value={`${stats.occupancy}%`} color="success" bg="#e8f5e9" />
              </Grid>
              <Grid item xs={6}>
                <StatCard label="Collected" value={formatCurrency(stats.rentCollected)} color="info" bg="#e3f2fd" />
              </Grid>
            </Grid>
          </ArgonBox>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={2} px={1} width="100%" maxWidth="1400px" mx="auto">
        <ArgonBox mb={3}>
          <ArgonTypography variant="h4" fontWeight="bold">
            Welcome, {getUserDisplayName()}
          </ArgonTypography>
        </ArgonBox>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={3}>
            <StatCard label="Properties" value={stats.properties} color="info" bg="#e0f7fa" />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard label="Occupancy" value={`${stats.occupancy}%`} color="success" bg="#e8f5e9" />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard label="Rent Collected" value={formatCurrency(stats.rentCollected)} color="secondary" bg="#f3e5f5" />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard label="Open Tickets" value={stats.openTickets} color="warning" bg="#fff3e0" />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Quick Actions
                </ArgonTypography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <ArgonButton color="info" fullWidth href="/properties/add">
                      <Icon sx={{ mr: 1 }}>add_home</Icon> Add Property
                    </ArgonButton>
                  </Grid>
                  <Grid item xs={6}>
                    <ArgonButton color="success" fullWidth href="/tenants/directory">
                      <Icon sx={{ mr: 1 }}>person_add</Icon> Add Tenant
                    </ArgonButton>
                  </Grid>
                  <Grid item xs={6}>
                    <ArgonButton color="primary" fullWidth href="/quick-lease">
                      <Icon sx={{ mr: 1 }}>description</Icon> New Lease
                    </ArgonButton>
                  </Grid>
                  <Grid item xs={6}>
                    <ArgonButton color="warning" fullWidth href="/maintenance/requests">
                      <Icon sx={{ mr: 1 }}>build</Icon> Maintenance
                    </ArgonButton>
                  </Grid>
                </Grid>
              </ArgonBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Portfolio Summary
                </ArgonTypography>
                <ArgonBox display="flex" justifyContent="space-between" mb={1}>
                  <ArgonTypography variant="body2" color="text">Active Leases</ArgonTypography>
                  <ArgonTypography variant="body2" fontWeight="medium">{stats.activeLeases}</ArgonTypography>
                </ArgonBox>
                <ArgonBox display="flex" justifyContent="space-between" mb={1}>
                  <ArgonTypography variant="body2" color="text">Rent Outstanding</ArgonTypography>
                  <ArgonTypography variant="body2" fontWeight="medium" color="error">
                    {formatCurrency(stats.rentDue)}
                  </ArgonTypography>
                </ArgonBox>
                <ArgonBox display="flex" justifyContent="space-between" mb={1}>
                  <ArgonTypography variant="body2" color="text">Open Maintenance</ArgonTypography>
                  <ArgonTypography variant="body2" fontWeight="medium">{stats.openTickets}</ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Default;
